import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import DateField from './DateField';
import TeamField from './TeamField';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { errorCodesOf } from '../../services/api-error';
import {
  registrationsService,
  type EntryGender,
  type EntryPayload,
  type Registration,
  type RegistrationCategory,
  type RegistrationTeam,
} from '../../services/registrations';
import { choosesTurn, chosenTurn } from '../../components/trophy/entry-options';
import { useEntryOptions, type EntryOptionsFetcher } from '../../components/trophy/useEntryOptions';
import type { EntryRound } from '../../types/trophy';
import { isUnderConsentAge } from '../../utils/consent-age';
import GuardianEmailField from './GuardianEmailField';

/** Radix Select cannot hold an empty value, so "none chosen" carries a token. */
const NONE = '__none__';

const today = new Date();
const OLDEST_BIRTH_DATE = new Date(1920, 0, 1);

/** The turn refusals the form shows under the turn rather than in a toast. */
const TURN_CODES = ['turn_has_results', 'round_not_offered'];

/**
 * The desk's own `entry_options`: it answers before the competition is public.
 * Module-level, so the hook sees the same function on every render.
 */
const deskEntryOptions: EntryOptionsFetcher = (slug, details) =>
  registrationsService.entryOptions(slug, details);

/** What the form holds: the payload, with the gender and the turn always there. */
type EntryForm = EntryPayload & { gender: EntryGender | ''; competition_round_id: string };

const EMPTY: EntryForm = {
  name: '',
  surname: '',
  email: '',
  guardian_email: '',
  birth_date: '',
  competition_category_id: '',
  competition_team_id: '',
  gender: '',
  competition_round_id: '',
};

/**
 * An entry as the form opens it. Where the organiser's strategy gives the
 * classes, a class it gave reads "automatic" (blank) and only one somebody
 * chose is shown as picked — so correcting the date moves the first and never
 * the second, exactly as the server will.
 */
function fromRegistration(registration: Registration, assignsCategories: boolean): EntryForm {
  const automatic = assignsCategories && !registration.category_chosen;

  return {
    name: registration.name ?? '',
    surname: registration.surname ?? '',
    email: registration.email ?? '',
    guardian_email: registration.guardian_email ?? '',
    birth_date: registration.birth_date ?? '',
    competition_category_id: automatic ? '' : registration.category_id ?? '',
    competition_team_id: registration.team_id ?? '',
    gender: registration.gender ?? '',
    competition_round_id: registration.competition_round_id ?? '',
  };
}

/** A date of birth the server can work a class out from: whole, and not in the future. */
function settledBirthDate(value: string | null | undefined): string | null {
  const date = (value ?? '').slice(0, 10);
  const todayIso = new Date().toISOString().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) && date <= todayIso ? date : null;
}

interface EntrantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stageSlug: string;
  /** Null when somebody new is being taken on at the desk. */
  registration: Registration | null;
  categories: RegistrationCategory[];
  teams: RegistrationTeam[];
  /** A squad written from inside the form, so the page's list grows with it. */
  onTeamCreated: (team: RegistrationTeam) => void;
  /** Whether this viewer may still change the entry list at all. */
  deskOpen?: boolean;
  /**
   * Whether an entry here may hold the gender of its class — the organiser
   * signed the agreement that covers it. The field is there only then.
   */
  genderCollected?: boolean;
  /** Whether a category left blank is the organiser's strategy's to give ("automatic"). */
  assignsCategories?: boolean;
  onSaved: (registration: Registration) => void;
  /** Withdrawing lives in here, behind the same "open the entry" gesture. */
  onDeleted?: (registration: Registration) => void;
}

/**
 * Taking somebody on at the desk, and correcting them afterwards.
 *
 * An entry stands on its own — a name, a squad, a category and an address — and
 * that address is not just a field: entering somebody sends them an invitation,
 * with a token to open an account if they have none. So the email is asked for
 * up front rather than left as an afterthought.
 *
 * Under 14 the address is the guardian's: the date of birth decides, and once
 * it says so the form asks for the parent's email instead and lets the child's
 * own go. The server holds the same rule. That is why the date is asked for
 * before any address — fill it in and the form settles whose it wants.
 *
 * What it asks for is deliberately short: a name, somewhere to write, a squad,
 * a class and the date of birth that settles the class. An entry holds no tax
 * code, no federation card number and no certificate expiry — see
 * ReduceCompetitionEntryData on the server for why they went, and a gym's
 * roster for where that paperwork still lives.
 *
 * Everything here is the entry's own: there is no roster behind it, so what the
 * desk types is what the competition holds. At a circuit, the same person's
 * details can be corrected once for every stage from the trophy's participants
 * section instead.
 *
 * Withdrawing somebody is here too, rather than on the start list itself: it is
 * the one thing on this page that cannot be undone, and a bin next to a row of
 * names is a mis-click away from losing an entry.
 *
 * **Gender, category and turn.** Where the organiser's agreement covers it the
 * form asks the gender of the entrant's class — never required: the desk is
 * never held to it. Where the organiser picked a strategy, the category list
 * opens on "automatic", and the line under it says which class the server
 * gives for the date and gender typed (the desk's `entry_options`); a class
 * picked from the list stays as it is. When that class is split over two or
 * more turns the desk may book one, or leave it to choose — the athlete is
 * then on every turn of the class. A turn the entry already has results on is
 * not left: the server refuses, and the reason lands under the turn.
 */
export default function EntrantDialog({
  open,
  onOpenChange,
  stageSlug,
  registration,
  categories,
  teams,
  onTeamCreated,
  deskOpen = true,
  genderCollected = false,
  assignsCategories = false,
  onSaved,
  onDeleted,
}: EntrantDialogProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [form, setForm] = useState<EntryForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resending, setResending] = useState(false);
  const [turnError, setTurnError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(registration ? fromRegistration(registration, assignsCategories) : EMPTY);
    setTurnError(null);
  }, [open, registration, assignsCategories]);

  const setField = (field: keyof EntryForm, value: string) =>
    setForm((current) => ({ ...current, [field]: value }));

  // The class and its turns, read from the server as the values change: a
  // class picked from the list is kept as it is (and needs nothing else); left
  // automatic, it is worked out once the date is whole.
  const pickedCategory = form.competition_category_id || null;
  const birthDate = settledBirthDate(form.birth_date);
  const gender = genderCollected && form.gender ? form.gender : null;
  const { options, loading: optionsLoading } = useEntryOptions(
    open ? stageSlug : null,
    pickedCategory
      ? { birthDate: null, gender: null, categoryId: pickedCategory }
      : assignsCategories && birthDate
        ? { birthDate, gender, categoryId: null }
        : null,
    deskEntryOptions
  );
  const choosingTurn = choosesTurn(options);
  const turns = choosingTurn && options ? options.bookable_rounds : [];
  const bookedTurn = chosenTurn(options, form.competition_round_id || null);

  const minor = isUnderConsentAge(form.birth_date);
  // Typed in by a squad's team manager: it may carry no address at all, and a
  // child's row carries only the guardian's (the server refuses the child's).
  const teamEntry = !!registration?.team_entered_at;
  const canSave =
    (form.name ?? '').trim().length > 0 &&
    (!minor || teamEntry || (form.guardian_email ?? '').trim().length > 0) &&
    !optionsLoading;

  const roundTime = (round: EntryRound) =>
    round.start_time
      ? new Date(round.start_time).toLocaleString(i18n.language, {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })
      : t('stageEntry.dialog.timeTba');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave || saving) return;

    const payload: EntryPayload = {
      ...form,
      // A squad's row keeps only the address we would write to — the athlete's
      // from 14, the guardian's below — so a date that crossed 14 drops the other.
      ...(teamEntry ? (minor ? { email: '' } : { guardian_email: '' }) : {}),
    };
    // Asked only where the agreement covers it; elsewhere the server drops it.
    if (!genderCollected) delete payload.gender;
    // Only when the turns were on screen to pick from. Blank is "to choose";
    // left out, the booking stays as it is.
    if (choosingTurn) payload.competition_round_id = bookedTurn ?? '';
    else delete payload.competition_round_id;

    setSaving(true);
    setTurnError(null);
    try {
      const saved = registration
        ? await registrationsService.update(stageSlug, registration.id, payload)
        : await registrationsService.create(stageSlug, payload);

      onSaved(saved);
      onOpenChange(false);
    } catch (error) {
      // A refused turn is said under the turn, where the desk is looking.
      const turnCode = errorCodesOf(error).find((code) => TURN_CODES.includes(code));
      if (turnCode && choosingTurn) {
        setTurnError(t(`gymAdmin.registrations.turnErrors.${turnCode}`));
        return;
      }
      // The server says why in the cases that matter — the desk is closed, the
      // address is already entered — and that is more use than a generic line.
      toast({
        title: t('gymAdmin.registrations.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  /** The line under an "automatic" category: what the strategy gives, or why nothing yet. */
  const automaticLine = (): string | null => {
    if (!assignsCategories || pickedCategory) return null;
    if (optionsLoading && !options) return t('gymAdmin.registrations.categoryAutoLoading');
    if (!options || options.category_state === 'needs_data') return t('gymAdmin.registrations.categoryAutoNeedsData');
    if (options.category) return t('gymAdmin.registrations.categoryAutoResult', { name: options.category.name });
    return t('gymAdmin.registrations.categoryAutoNoMatch');
  };
  const automatic = automaticLine();

  /**
   * The letter again. What a desk actually needs when an address was mistyped
   * and corrected, when somebody says nothing arrived, or when a parent who
   * dealt with one child never heard about the second — the circuit stays
   * quiet about a letter it has already written.
   */
  const handleResend = async () => {
    if (!registration || resending) return;

    setResending(true);
    try {
      const saved = await registrationsService.resendInvitation(stageSlug, registration.id);
      onSaved(saved);
      toast({
        title: t('gymAdmin.registrations.invitationSent', {
          email: registration.minor ? registration.guardian_email : registration.email,
        }),
      });
    } catch (error) {
      toast({
        title: t('gymAdmin.registrations.invitationSendError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setResending(false);
    }
  };

  const handleDelete = async () => {
    if (!registration || !onDeleted || deleting) return;
    if (!window.confirm(t('gymAdmin.registrations.confirmWithdraw', { name: registration.full_name })))
      return;

    setDeleting(true);
    try {
      await registrationsService.remove(stageSlug, registration.id);
      onDeleted(registration);
      onOpenChange(false);
    } catch (error) {
      toast({
        title: t('gymAdmin.registrations.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] grid-cols-[minmax(0,1fr)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {registration
              ? t('gymAdmin.registrations.editEntrantTitle')
              : t('gymAdmin.registrations.newEntrantTitle')}
          </DialogTitle>
          <DialogDescription>{t('gymAdmin.registrations.entrantFormDescription')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="entrant-name">{t('gymAdmin.members.fields.name')}</Label>
              <Input
                id="entrant-name"
                value={form.name ?? ''}
                onChange={(e) => setField('name', e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="entrant-surname">{t('gymAdmin.members.fields.surname')}</Label>
              <Input
                id="entrant-surname"
                value={form.surname ?? ''}
                onChange={(e) => setField('surname', e.target.value)}
              />
            </div>
          </div>

          {/* Straight after the name, because it is what decides whose
              address the form asks for next: under 14 the letter goes to a
              parent. It also settles the class the entrant runs in. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <DateField
              id="entrant-birth-date"
              label={t('gymAdmin.members.fields.birthDate')}
              value={form.birth_date ?? ''}
              onChange={(value) => setField('birth_date', value)}
              captionLayout="dropdown"
              startMonth={OLDEST_BIRTH_DATE}
              endMonth={today}
            />

            {/* The gender of the class, beside the date that with it settles
                the class. Never required: the desk is never held to it. */}
            {genderCollected && (
              <div className="space-y-2">
                <Label htmlFor="entrant-gender">{t('gymAdmin.registrations.gender')}</Label>
                <Select
                  value={form.gender || NONE}
                  onValueChange={(value) => setField('gender', value === NONE ? '' : value)}
                >
                  <SelectTrigger id="entrant-gender" aria-label={t('gymAdmin.registrations.gender')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t('gymAdmin.registrations.genderNotSet')}</SelectItem>
                    <SelectItem value="male">{t('gymAdmin.registrations.genders.male')}</SelectItem>
                    <SelectItem value="female">{t('gymAdmin.registrations.genders.female')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{t('gymAdmin.registrations.genderHint')}</p>
              </div>
            )}
          </div>

          {/* Not an afterthought: this address is what the invitation goes to
              — unless the entrant is under 14, when it is the guardian's. */}
          {minor && (
            <GuardianEmailField
              id="entrant-guardian-email"
              value={form.guardian_email ?? ''}
              onChange={(value) => setField('guardian_email', value)}
              required={!teamEntry}
            />
          )}
          {!(minor && teamEntry) && (
            <div className="space-y-2">
              <Label htmlFor="entrant-email">
                {minor ? t('gymAdmin.registrations.athleteEmail') : t('gymAdmin.members.fields.email')}
              </Label>
              <Input
                id="entrant-email"
                type="email"
                value={form.email ?? ''}
                onChange={(e) => setField('email', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {minor
                  ? t('gymAdmin.registrations.athleteEmailHelp')
                  : teamEntry
                    ? t('gymAdmin.registrations.teamEntryEmailHelp')
                    : t('gymAdmin.registrations.emailHelp')}
              </p>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            {/* The squad they turn up with, picked from the season's list or
                written here — see TeamField for why the desk may write one. */}
            <TeamField
              id="entrant-team"
              value={form.competition_team_id ?? ''}
              teams={teams}
              onChange={(teamId) => setField('competition_team_id', teamId)}
              ownerSlug={stageSlug}
              owner="competition"
              onCreated={onTeamCreated}
            />

            {categories.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="entrant-category">{t('gymAdmin.members.fields.category')}</Label>
                <Select
                  value={form.competition_category_id || NONE}
                  onValueChange={(value) =>
                    setField('competition_category_id', value === NONE ? '' : value)
                  }
                >
                  <SelectTrigger id="entrant-category" aria-label={t('gymAdmin.members.fields.category')}>
                    <SelectValue placeholder={t('gymAdmin.members.fields.noCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Where a strategy gives the classes, leaving it blank is
                        asking for the class it gives — not for none. */}
                    <SelectItem value={NONE}>
                      {assignsCategories
                        ? t('gymAdmin.registrations.categoryAuto')
                        : t('gymAdmin.members.fields.noCategory')}
                    </SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {automatic && (
                  <p className="text-xs text-muted-foreground" data-testid="entrant-category-auto">
                    {automatic}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* The class is split over two or more turns: the desk may book one,
              or leave it to choose — the athlete is then on all of them. */}
          {choosingTurn && (
            <div className="space-y-2" data-testid="entrant-turn">
              <Label htmlFor="entrant-turn">{t('gymAdmin.registrations.turn')}</Label>
              <Select
                value={bookedTurn ?? NONE}
                onValueChange={(value) => {
                  setField('competition_round_id', value === NONE ? '' : value);
                  setTurnError(null);
                }}
              >
                <SelectTrigger id="entrant-turn" aria-label={t('gymAdmin.registrations.turn')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('gymAdmin.registrations.turnToChoose')}</SelectItem>
                  {/* A full turn is still the desk's to book — the limit is
                      what athletes book against — so it is only marked. */}
                  {turns.map((round) => (
                    <SelectItem key={round.id} value={round.id}>
                      {t('gymAdmin.registrations.turnLine', { name: round.name, time: roundTime(round) })}
                      {round.full ? ` · ${t('stageEntry.dialog.full')}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {turnError ? (
                <p className="text-xs text-destructive" data-testid="entrant-turn-error">
                  {turnError}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">{t('gymAdmin.registrations.turnHint')}</p>
              )}
            </div>
          )}

          {/* An organiser on the older agreement: the entry holds neither, and
              the desk is told why rather than finding the fields missing. */}
          {!genderCollected && (
            <p className="text-xs text-muted-foreground" data-testid="entrant-gender-not-collected">
              {t('gymAdmin.registrations.genderNotCollected')}
            </p>
          )}


          {/* What has been said to whom, and the way to say it again. Only on
              an entry that exists and has somewhere to write to. */}
          {registration && deskOpen && (registration.email || registration.guardian_email) && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed p-3">
              <div className="min-w-0 text-xs text-muted-foreground">
                <p className="font-medium text-foreground">
                  {registration.minor
                    ? t('gymAdmin.registrations.guardianEmailShort', {
                        email: registration.guardian_email,
                      })
                    : registration.email}
                </p>
                <p>
                  {registration.guardian_declined
                    ? t('gymAdmin.registrations.guardianDeclined')
                    : registration.invited_at
                      ? t('gymAdmin.registrations.invitedOn', {
                          date: new Date(registration.invited_at).toLocaleDateString(),
                        })
                      : t('gymAdmin.registrations.neverInvited')}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={resending || saving}
                onClick={handleResend}
              >
                {resending ? <Loader2 className="size-4 animate-spin" /> : <Mail className="size-4" />}
                {t('gymAdmin.registrations.resendInvitation')}
              </Button>
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-between">
            {registration && onDeleted && deskOpen ? (
              <Button
                type="button"
                variant="destructive"
                className="gap-2 sm:mr-auto"
                disabled={deleting || saving}
                onClick={handleDelete}
              >
                {deleting ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Trash2 className="size-4" />
                )}
                {t('gymAdmin.registrations.withdraw')}
              </Button>
            ) : (
              <span />
            )}

            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={!canSave || saving}>
                {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
                {t('common.save')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
